import { useEffect, useState } from 'react';
import {
  api,
  ErrorApi,
  type EstadoRespaldo,
  type JugadorParaAdmin,
  type JugadorPublico,
  type Revision,
  type Yo,
} from '../services/api';
import { diasEntre, fechaHora, hoy } from '../nucleo/fechas';
import { Aviso, Boton, Cargando, Tarjeta, Titulo } from '../components/ui';
import { compartir, linkDelSitio, type ResultadoCompartir } from '../pwa/compartir';
import { NOMBRE_APP, LEMA_APP } from '../marca';

/**
 * Administración: dar de alta al grupo, corregir nombres, borrar al que se
 * cargó por error (solo si nunca jugó), dar de baja al que dejó de venir, y
 * rescatar al que se olvidó el PIN. Arriba de todo, lo que conviene revisar. Cada jugador también puede cambiarse su propio nombre desde
 * "Mi cuenta"; desde acá el admin corrige el de cualquiera.
 *
 * El PIN provisorio se muestra UNA sola vez, en el momento. No se guarda en
 * claro en ningún lado, así que si se cierra esta tarjeta sin copiarlo, hay
 * que resetearlo de nuevo. Es a propósito.
 */
export function Admin({ yo, onVerPartido, onLogros }: { yo: Yo; onVerPartido: (id: number) => void; onLogros: () => void }) {
  const [jugadores, setJugadores] = useState<JugadorParaAdmin[] | null>(null);
  const [revision, setRevision] = useState<Revision | null>(null);
  // A quien se esta por dar de baja: pide el segundo toque, como borrar.
  const [porBaja, setPorBaja] = useState<number | null>(null);
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // A quien se le esta cambiando el nombre, y el texto que se va escribiendo.
  const [editando, setEditando] = useState<{ id: number; nombre: string } | null>(null);

  // A quien se esta por borrar: pide confirmacion en el lugar, no se deshace.
  const [porBorrar, setPorBorrar] = useState<number | null>(null);

  // Qué pasó con el último "compartir", para decírselo a quien tocó el botón.
  const [compartido, setCompartido] = useState<ResultadoCompartir | null>(null);

  // El PIN recién generado, para copiar y pasar por WhatsApp.
  const [reciente, setReciente] = useState<{ nombre: string; pin: string } | null>(null);

  const traer = () =>
    api
      .jugadoresAdmin()
      .then((r) => setJugadores(r.jugadores))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));

  useEffect(() => {
    void traer();
    // Si falla, no se muestra la seccion: no es motivo para romper la pantalla.
    api.revisar().then(setRevision).catch(() => setRevision(null));
  }, []);

  async function darDeBaja(id: number, activo: boolean) {
    setOcupado(true);
    setError(null);
    try {
      await api.cambiarActivo(id, activo);
      setPorBaja(null);
      await traer();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo.');
    } finally {
      setOcupado(false);
    }
  }

  async function agregar() {
    const limpio = nombre.trim();
    if (!limpio) return;

    setOcupado(true);
    setError(null);
    try {
      const r = await api.altaJugador(limpio);
      setReciente({ nombre: r.jugador.nombre, pin: r.pin_provisorio });
      setNombre('');
      await traer();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo agregar.');
    } finally {
      setOcupado(false);
    }
  }

  async function resetear(jugador: JugadorPublico) {
    setOcupado(true);
    setError(null);
    try {
      const r = await api.resetearPin(jugador.id);
      setReciente({ nombre: jugador.nombre, pin: r.pin_provisorio });
      await traer();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo resetear.');
    } finally {
      setOcupado(false);
    }
  }

  async function renombrar() {
    if (!editando) return;
    setOcupado(true);
    setError(null);
    try {
      await api.renombrarJugador(editando.id, editando.nombre.trim());
      setEditando(null);
      await traer();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cambiar el nombre.');
    } finally {
      setOcupado(false);
    }
  }

  async function borrar(id: number) {
    setOcupado(true);
    setError(null);
    try {
      await api.borrarJugador(id);
      setPorBorrar(null);
      await traer();
    } catch (e) {
      // El caso tipico: ya jugo partidos. El servidor explica por que no.
      setError(e instanceof ErrorApi ? e.message : 'No se pudo borrar.');
      setPorBorrar(null);
    } finally {
      setOcupado(false);
    }
  }

  async function mandar(texto: string) {
    setCompartido(await compartir(NOMBRE_APP, texto));
  }

  const mensajeGrupo = [
    `${NOMBRE_APP} · ${LEMA_APP} 🎾`,
    `Entren desde acá: ${linkDelSitio()}`,
    'Conviene instalarla en el celular: así queda el ícono y no hay que buscar el link.',
  ].join('\n');

  const mensajeJugador = (quien: string, pin: string) =>
    [
      `${quien}, te sumé a ${NOMBRE_APP} 🎾`,
      `Entrá desde acá: ${linkDelSitio()}`,
      `Tu PIN provisorio es ${pin}. La primera vez te pide que elijas uno tuyo.`,
      'Instalala en el celular así queda el ícono.',
    ].join('\n');

  const avisoCompartido =
    compartido === 'copiado'
      ? 'Copiado. Pegalo en WhatsApp.'
      : compartido === 'fallo'
        ? 'No se pudo compartir ni copiar desde este navegador.'
        : null;

  if (!jugadores) return <Cargando que="Buscando jugadores" />;

  const activos = jugadores.filter((j) => j.activo === 1);
  const bajas = jugadores.filter((j) => j.activo !== 1);

  return (
    <div className="flex flex-col gap-5">
      {error && <Aviso tono="error">{error}</Aviso>}

      <Tarjeta className="flex items-center gap-3">
        <div className="flex-1"><p className="font-semibold">Logros del grupo</p><p className="text-xs text-(--color-apagado)">Creá logros automáticos o reconocimientos manuales.</p></div>
        <Boton chico onClick={onLogros}>Administrar</Boton>
      </Tarjeta>

      {/* Solo lo ve el admin, a proposito: son señales, no pruebas, y en la
          pantalla de todos se leerian como una acusacion. El detalle de cada
          partido si lo ve cualquiera. */}
      {revision && (revision.tarde.length > 0 || revision.rechazos.length > 0) && (
        <ParaRevisar revision={revision} onVerPartido={onVerPartido} />
      )}

      <Respaldo />

      <Tarjeta className="flex items-center gap-3">
        <p className="flex-1 text-sm text-(--color-apagado)">
          Mandale el link al grupo, así nadie lo tiene que escribir.
        </p>
        <Boton chico onClick={() => void mandar(mensajeGrupo)}>
          Compartir link
        </Boton>
      </Tarjeta>

      {avisoCompartido && <Aviso>{avisoCompartido}</Aviso>}

      {reciente && (
        <Tarjeta className="border-(--color-acento)">
          <Titulo>PIN de {reciente.nombre}</Titulo>
          <p className="my-3 text-center text-4xl font-bold tracking-[0.3em] tabular-nums">
            {reciente.pin}
          </p>
          <p className="text-sm text-(--color-apagado)">
            Mandáselo por WhatsApp: el botón de abajo arma el mensaje con el link y el PIN. La
            primera vez que entre, la app le pide que elija uno propio.
          </p>
          <p className="mt-2 text-sm text-(--color-alerta)">
            Esto se muestra una sola vez. Si lo cerrás sin copiarlo, hay que resetearlo de nuevo.
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <Boton ancho onClick={() => void mandar(mensajeJugador(reciente.nombre, reciente.pin))}>
              Mandárselo (link + PIN)
            </Boton>
            <Boton tipo="secundario" ancho onClick={() => setReciente(null)}>
              Listo, ya lo tiene
            </Boton>
          </div>
        </Tarjeta>
      )}

      <section>
        <Titulo>Agregar un jugador</Titulo>
        <div className="mt-3 flex gap-2">
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void agregar();
            }}
            placeholder="Nombre"
            maxLength={40}
            className="min-h-12 min-w-0 flex-1 rounded-xl border border-(--color-borde)
              bg-(--color-panel) px-4 text-base outline-none
              focus:border-(--color-acento)"
          />
          <Boton onClick={agregar} deshabilitado={ocupado || nombre.trim().length < 2}>
            Agregar
          </Boton>
        </div>
        <p className="mt-2 text-xs text-(--color-apagado)">
          Si hay dos con el mismo nombre, agregale el apellido o el apodo ("Martín F.").
          Conviene decidirlo ahora: cambiarlo después es fácil, pero separar a dos jugadores
          que ya tienen historial no.
        </p>
      </section>

      <section>
        <Titulo chico>
          {activos.length} jugador{activos.length === 1 ? '' : 'es'}
        </Titulo>
        <ul className="mt-3 flex flex-col gap-2">
          {activos.map((jugador) => (
            <li key={jugador.id}>
              {editando?.id === jugador.id ? (
                <Tarjeta className="flex items-center gap-2 py-3">
                  <input
                    autoFocus
                    value={editando.nombre}
                    onChange={(e) => setEditando({ id: jugador.id, nombre: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void renombrar();
                      if (e.key === 'Escape') setEditando(null);
                    }}
                    maxLength={40}
                    className="min-h-10 min-w-0 flex-1 rounded-lg border border-(--color-borde)
                      bg-(--color-fondo) px-3 outline-none focus:border-(--color-acento)"
                  />
                  <Boton
                    chico
                    onClick={renombrar}
                    deshabilitado={ocupado || editando.nombre.trim().length < 2}
                  >
                    Guardar
                  </Boton>
                  <Boton tipo="secundario" chico onClick={() => setEditando(null)}>
                    ✕
                  </Boton>
                </Tarjeta>
              ) : (
                <Tarjeta className="flex flex-wrap items-center gap-2 py-3">
                  {/* flex-wrap: si el nombre es largo, los botones bajan a otra
                      linea en vez de dejarlo en 60 px partido en tres. */}
                  <span className="min-w-[9rem] flex-1">
                    {jugador.nombre}
                    {jugador.id === yo.jugador.id && (
                      <span className="ml-2 text-xs text-(--color-apagado)">vos</span>
                    )}
                    {jugador.pin_provisorio === 1 && (
                      <span className="ml-2 text-xs text-(--color-alerta)">
                        todavía no entró
                      </span>
                    )}
                    <span className="block text-xs text-(--color-apagado)">
                      {ultimaVez(jugador)}
                    </span>
                  </span>
                  {porBaja === jugador.id ? (
                    <>
                      <span className="text-xs text-(--color-apagado)">
                        ¿Darlo de baja? Sale de la tabla y no puede entrar; su historia queda.
                      </span>
                      <Boton
                        tipo="peligro"
                        chico
                        onClick={() => darDeBaja(jugador.id, false)}
                        deshabilitado={ocupado}
                      >
                        Sí, dar de baja
                      </Boton>
                      <Boton tipo="secundario" chico onClick={() => setPorBaja(null)}>
                        No
                      </Boton>
                    </>
                  ) : porBorrar === jugador.id ? (
                    <>
                      <span className="text-xs text-(--color-apagado)">¿Borrarlo?</span>
                      <Boton
                        tipo="peligro"
                        chico
                        onClick={() => borrar(jugador.id)}
                        deshabilitado={ocupado}
                      >
                        Sí, borrar
                      </Boton>
                      <Boton tipo="secundario" chico onClick={() => setPorBorrar(null)}>
                        No
                      </Boton>
                    </>
                  ) : (
                    <>
                      <Boton
                        tipo="secundario"
                        chico
                        onClick={() => setEditando({ id: jugador.id, nombre: jugador.nombre })}
                        deshabilitado={ocupado}
                      >
                        Nombre
                      </Boton>
                      <Boton
                        tipo="secundario"
                        chico
                        onClick={() => resetear(jugador)}
                        deshabilitado={ocupado}
                      >
                        PIN nuevo
                      </Boton>
                      {/* A uno mismo no: el servidor tampoco lo deja. Borrar solo
                          anda con quien nunca jugo; si jugo, el servidor lo
                          rechaza y explica por que. */}
                      {/* Borrar es para el cargado por error, que nunca jugo;
                          al que ya jugo se lo da de baja. A uno mismo y a un
                          admin, ninguna de las dos: el servidor tampoco deja. */}
                      {jugador.id !== yo.jugador.id &&
                        jugador.rol !== 'admin' &&
                        (jugador.partidos === 0 ? (
                          <Boton
                            tipo="peligro"
                            chico
                            onClick={() => setPorBorrar(jugador.id)}
                            deshabilitado={ocupado}
                          >
                            Borrar
                          </Boton>
                        ) : (
                          <Boton
                            tipo="peligro"
                            chico
                            onClick={() => setPorBaja(jugador.id)}
                            deshabilitado={ocupado}
                          >
                            Baja
                          </Boton>
                        ))}
                    </>
                  )}
                </Tarjeta>
              )}
            </li>
          ))}
        </ul>
      </section>

      {bajas.length > 0 && (
        <section>
          <Titulo chico>Dados de baja</Titulo>
          <ul className="mt-3 flex flex-col gap-2">
            {bajas.map((jugador) => (
              <li key={jugador.id}>
                <Tarjeta className="flex items-center gap-2 py-3 opacity-80">
                  <span className="min-w-0 flex-1">
                    {jugador.nombre}
                    <span className="block text-xs text-(--color-apagado)">
                      {ultimaVez(jugador)}
                    </span>
                  </span>
                  <Boton
                    tipo="secundario"
                    chico
                    onClick={() => darDeBaja(jugador.id, true)}
                    deshabilitado={ocupado}
                  >
                    Reactivar
                  </Boton>
                </Tarjeta>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ParaRevisar({
  revision,
  onVerPartido,
}: {
  revision: Revision;
  onVerPartido: (id: number) => void;
}) {
  return (
    <Tarjeta className="border-(--color-alerta)">
      <Titulo>Para revisar</Titulo>
      <p className="mt-1 text-xs text-(--color-apagado)">
        Últimos 60 días. No quiere decir que esté mal: es lo que conviene mirar.
      </p>

      {revision.tarde.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-semibold">Cargados varios días después</p>
          <ul className="mt-1 flex flex-col">
            {revision.tarde.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => onVerPartido(p.id)}
                  className="flex min-h-10 w-full items-center justify-between gap-2 text-left text-sm"
                >
                  <span>
                    {fechaCorta(p.jugado_el)} · lo cargó {p.cargado_por_nombre}{' '}
                    <span className="text-(--color-alerta)">{p.dias} días después</span>
                    {p.estado !== 'confirmado' && (
                      <span className="text-(--color-apagado)"> · {p.estado}</span>
                    )}
                  </span>
                  <span className="text-(--color-apagado)" aria-hidden>
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {revision.rechazos.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-semibold">Con varios partidos rechazados</p>
          <ul className="mt-1 flex flex-col gap-1 text-sm">
            {revision.rechazos.map((r) => (
              <li key={r.jugador_id}>
                {r.nombre}:{' '}
                <span className="text-(--color-alerta)">{r.rechazados} rechazados</span> de{' '}
                {r.cargados} que cargó
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-(--color-apagado)">
            Aparecen tachados en Inicio → Ver todos los partidos.
          </p>
        </div>
      )}
    </Tarjeta>
  );
}

/**
 * El respaldo semanal: cuándo fue el último, y avisa fuerte si se atrasó.
 * Un respaldo que falla sin que nadie se entere es peor que no tener ninguno.
 */
function Respaldo() {
  const [estado, setEstado] = useState<EstadoRespaldo | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.respaldo().then(setEstado).catch(() => setEstado(null));
  }, []);

  if (!estado) return null;

  async function ahora() {
    setOcupado(true);
    setError(null);
    try {
      setEstado(await api.respaldarAhora());
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo hacer el respaldo.');
    } finally {
      setOcupado(false);
    }
  }

  if (!estado.configurado) {
    return (
      <Aviso tono="alerta">
        Los respaldos automáticos no están activos: falta el lugar donde guardarlos en
        Cloudflare.
      </Aviso>
    );
  }

  const dias = estado.ultimo === null ? null : Math.floor((Date.now() / 1000 - estado.ultimo) / 86_400);
  const atrasado = dias === null || dias >= estado.dias_para_avisar;
  const cuando = estado.ultimo === null ? null : fechaHora(estado.ultimo);

  return (
    <Tarjeta className={atrasado ? 'border-(--color-alerta)' : ''}>
      <p className={`text-sm font-semibold ${atrasado ? 'text-(--color-alerta)' : ''}`}>
        {dias === null
          ? 'Todavía no se hizo ningún respaldo'
          : atrasado
            ? `Hace ${dias} días que no hay respaldo`
            : 'Respaldo al día'}
      </p>
      <p className="mt-1 text-xs text-(--color-apagado)">
        {cuando
          ? `El último: ${cuando.dia} a las ${cuando.hora}${estado.bytes ? ` · ${Math.max(1, Math.round(estado.bytes / 1024))} KB` : ''}. `
          : ''}
        Se hace solo una vez por semana y queda guardado en Cloudflare.
        {atrasado && ' Si sigue sin hacerse, avisá: algo está fallando.'}
      </p>
      {error && <p className="mt-2 text-xs text-(--color-error)">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Boton chico tipo="secundario" onClick={ahora} deshabilitado={ocupado}>
          Hacer uno ahora
        </Boton>
        {estado.ultimo !== null && (
          <a
            href={api.linkRespaldo()}
            className="flex min-h-10 items-center rounded-xl border border-(--color-borde)
              bg-(--color-panel) px-3 text-sm"
          >
            Descargar el último
          </a>
        )}
      </div>
    </Tarjeta>
  );
}

/** Para decidir una baja: cuánto jugó y hace cuánto que no viene. */
function ultimaVez(j: JugadorParaAdmin): string {
  if (!j.ultimo) return 'Todavía no jugó';
  const dias = diasEntre(j.ultimo, hoy());
  const cuando = dias === 0 ? 'hoy' : dias === 1 ? 'ayer' : `hace ${dias} días`;
  return `${j.partidos} partido${j.partidos === 1 ? '' : 's'} · el último, ${cuando}`;
}

function fechaCorta(fecha: string): string {
  const [, mes, dia] = fecha.split('-');
  return `${Number(dia)}/${Number(mes)}`;
}
